export const getPackPrioritizedServiceTypes = (
    serviceTypes: string[],
    packAppointmentTypes: string[][] | undefined,
): string[] => {
    if (!packAppointmentTypes?.length) {
        return serviceTypes;
    }

    const prioritizedTypes = new Set<string>();

    packAppointmentTypes.forEach(appointmentTypes => {
        appointmentTypes.forEach(type => {
            if (serviceTypes.includes(type)) {
                prioritizedTypes.add(type);
            }
        });
    });

    if (!prioritizedTypes.size) {
        return serviceTypes;
    }

    const prioritized = serviceTypes.filter(type => prioritizedTypes.has(type));
    const remaining = serviceTypes.filter(type => !prioritizedTypes.has(type));

    return [...prioritized, ...remaining];
};

export const getPackServiceTypes = (
    serviceTypes: string[],
    packAppointmentTypes: string[][] | undefined,
): string[] => {
    if (!packAppointmentTypes?.length) {
        return [];
    }

    const prioritizedTypes = new Set<string>();

    packAppointmentTypes.forEach(appointmentTypes => {
        appointmentTypes.forEach(type => {
            if (serviceTypes.includes(type)) {
                prioritizedTypes.add(type);
            }
        });
    });

    return serviceTypes.filter(type => prioritizedTypes.has(type));
};

export interface AppointmentTypeDisplayOptions {
    orderedServiceTypes: string[];
    highlightedServiceTypes: string[];
    groupByServiceType?: Record<string, string>;
}

export const buildAppointmentTypeDisplayOptions = (
    serviceTypes: string[],
    packAppointmentTypes: string[][] | undefined,
    categoryByServiceType: Record<string, string | undefined>,
    otherGroupLabel: string,
    packGroupLabel: string,
): AppointmentTypeDisplayOptions => {
    const prioritizedServiceTypes = getPackServiceTypes(serviceTypes, packAppointmentTypes);
    const resolveCategory = (type: string) => categoryByServiceType[type]?.trim() || otherGroupLabel;
    const hasAnyCategory = serviceTypes.some(type => !!categoryByServiceType[type]?.trim());

    if (!hasAnyCategory) {
        return {
            orderedServiceTypes: getPackPrioritizedServiceTypes(serviceTypes, packAppointmentTypes),
            highlightedServiceTypes: prioritizedServiceTypes,
        };
    }

    const prioritizedSet = new Set(prioritizedServiceTypes);
    const orderedRemainingServiceTypes = serviceTypes
        .filter(type => !prioritizedSet.has(type))
        .sort((a, b) => resolveCategory(a).localeCompare(resolveCategory(b)));

    const orderedServiceTypes = [...prioritizedServiceTypes, ...orderedRemainingServiceTypes];
    const groupByServiceType = Object.fromEntries(
        orderedServiceTypes.map(type => [
            type,
            prioritizedSet.has(type) ? packGroupLabel : resolveCategory(type),
        ]),
    );

    return {
        orderedServiceTypes,
        highlightedServiceTypes: prioritizedServiceTypes,
        groupByServiceType,
    };
};
